import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000IdColumnDef, type U2000CriteriaLookupConfig, type U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU3121Context } from "../U3121Context";
import { getNestedValue } from "../../u2000Core/tools/U2000ObjectUtils";
import {
  doDeleteData,
  doExportExcel,
  doSearch,
  luHandleAppiError,
  U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION,
  u3121FormatProjectLookupLabel,
  type U3121Employee,
  type U3121Project,
} from "../U3121Service";
import { NS, U3121ROLES } from "../U3121";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";
import U3121ProjectDeleteDlg from "./common/U3121ProjectDeleteDlg";
import { U3121ProjectListToolBar } from "./list/U3121ProjectListToolBar";
import { U3121UploadProjectFromFileDialog } from "./list/U3121UploadProjectFromFileDialog";
import { U3121UploadProjectFromFileRecapDialog } from "./list/U3121UploadProjectFromFileRecapDialog";
import {
  u3121ExtractSuccessProjetNumbersFromUploadResults,
  type U3121UploadFileLineResult,
} from "../tools/U3121UploadProjectFromFileBatchTools";
import type { Criteria } from "../../u2000Core/reducer/formReducer/U2000SearchState";
import { generateTemporaryId } from "../../u2000Core/tools/U2000IdUtils";
import {
  U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
  U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER,
  U2000_CRITERIA_LOOKUP_U3121_FIND_LAND_SURVEYOR,
  U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT_MANAGER,
  defaultItemToDisplay,
  defaultItemToValue,
  getCriteriaApiFieldName,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import {
  useU3121FindCustomerHandlers,
  useU3121FindLandSurveyorHandlers,
  useU3121FindProjectHandlers,
  useU3121FindProjectManagerHandlers,
} from "../U3121UseFindHandlers";

function formatEmployeeCell(value: unknown): string {
  const e = value as U3121Employee | null | undefined;
  if (!e || typeof e !== "object") return "";
  const code = e.codeEmployee?.trim() ?? "";
  const firstName = e.firstName?.trim() ?? "";
  const lastName = e.lastName?.trim() ?? "";
  const name = `${firstName} ${lastName}`.trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || "";
}

function displayEmployeeNestedColumn(column: U2000TableColumn, _value: unknown, row: Record<string, unknown>): string | null {
  const employee = getNestedValue(row, column.name) as U3121Employee | null | undefined;
  const label = formatEmployeeCell(employee);
  return label || null;
}

function u3121EmployeeLookupItemToSelectedDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  return formatEmployeeCell(item);
}

/** Affichage findCustomer : `customerDisplay` (code - nom) ou repli sur code/nom. */
function u3121CustomerLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  if (!item || typeof item !== "object") return "";
  const rec = item as Record<string, unknown>;
  const display = String(rec.customerDisplay ?? "").trim();
  if (display) return display;
  const code = String(rec.customerCode ?? "").trim();
  const name = String(rec.customerName ?? "").trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || "";
}

function u3121CustomerLookupItemToSelectedDisplay(item: unknown, config: U2000CriteriaLookupConfig): string {
  return u3121CustomerLookupItemToDisplay(item, config);
}

/** Affichage findProject : `projetNumber — projetName` (+ suffixe inactif si projectStatus === false). */
function u3121ProjectLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig, inactiveSuffix: string): string {
  return u3121FormatProjectLookupLabel(item as Record<string, unknown>, inactiveSuffix);
}

function u3121ProjectLookupItemToSelectedDisplay(
  item: unknown,
  config: U2000CriteriaLookupConfig,
  inactiveSuffix: string,
): string {
  return u3121ProjectLookupItemToDisplay(item, config, inactiveSuffix);
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "projetNumber",
      label: t("lblProject", { ns: NS }),
      widthClass: "w-[120px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
        searchCriterionField: "projetNumber",
        valueKey: "projetNumber",
        displayKeys: ["projetNumber", "projetName"],
        selectedDisplayKeys: ["projetNumber", "projetName"],
        placeholder: t("phCriteriaProject", { ns: "common" }),
      },
    },
    {
      name: "projetName",
      label: t("lblProjetName", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: "customer",
      label: t("lblCustomer", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      display: (_column, value) => {
        const c = value as { customerCode?: string; customerName?: string } | null | undefined;
        if (!c) return "";
        const code = c.customerCode?.trim() ?? "";
        const name = c.customerName?.trim() ?? "";
        if (code && name) return `${code} - ${name}`;
        return code || name || "";
      },
    },
    {
      name: "customer.customerCode",
      label: t("lblCustomer", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER,
        searchCriterionField: "search",
        valueKey: "customerCode",
        criteriaField: "customer.customerCode",
        displayKeys: ["customerDisplay"],
        selectedDisplayKeys: ["customerDisplay"],
        placeholder: t("phCriteriaCustomer", { ns: "common" }),
      },
    },
    {
      name: "billingProfile.fixedPrice",
      label: t("lblProjectFixedPrice", { ns: NS }),
      widthClass: "w-[120px]",
      type: "boolean",
      align: "center",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: ["equals", "notequals"],
      display: (_column, value) =>
        value === true || value === "true"
          ? t("lblBillableYes", { ns: NS })
          : value === false || value === "false"
            ? t("lblBillableNo", { ns: NS })
            : "",
    },
    {
      name: "billingProfile.billable",
      label: t("lblBillable", { ns: NS }),
      widthClass: "w-[100px]",
      type: "boolean",
      align: "center",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: ["equals", "notequals"],
      display: (_column, value) =>
        value === true || value === "true"
          ? t("lblBillableYes", { ns: NS })
          : value === false || value === "false"
            ? t("lblBillableNo", { ns: NS })
            : "",
    },
    {
      name: "projectManager",
      label: t("lblProjectLead", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: false,
      sortable: false,
      noTruncate: true,
      display: displayEmployeeNestedColumn,
    },
    {
      name: "projectManager.codeEmployee",
      label: t("lblProjectLead", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT_MANAGER,
        searchCriterionField: "codeEmployee",
        valueKey: "codeEmployee",
        criteriaField: "projectManager.codeEmployee",
        displayKeys: ["codeEmployee", "firstName", "lastName"],
        placeholder: t("phCriteriaEmployee", { ns: "common" }),
      },
    },
    {
      name: "projectToSurvey.surveyor",
      label: t("lblSurveyor", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: false,
      sortable: false,
      noTruncate: true,
      display: displayEmployeeNestedColumn,
    },
    {
      name: "projectToSurvey.surveyor.codeEmployee",
      label: t("lblSurveyor", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_LAND_SURVEYOR,
        searchCriterionField: "codeEmployee",
        valueKey: "codeEmployee",
        criteriaField: "projectToSurvey.surveyor.codeEmployee",
        displayKeys: ["codeEmployee", "firstName", "lastName"],
        placeholder: t("phCriteriaEmployee", { ns: "common" }),
      },
    },
    {
      name: "scopePeriods.startDate",
      label: t("lblProjectOpeningDate", { ns: NS }),
      widthClass: "w-[120px]",
      type: "date",
      isCriteriaField: true,
      inGridField: false,
      sortable: false,
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
    },
    {
      name: "projectStatus",
      label: t("lblProjectStatus", { ns: NS }),
      widthClass: "w-[100px]",
      type: "boolean",
      align: "center",
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      display: (_column, value, _row) => {
        return value ? (
          <span className="rounded-sm bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">{t("valActive", { ns: NS })}</span>
        ) : (
          <span className="rounded-sm bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">{t("valInactive", { ns: NS })}</span>
        );
      },
    },
    {
      name: "createdAt",
      label: t("lblFldCreatedAt", { ns: NS }),
      widthClass: "w-[120px]",
      isDefaultColumn: true,
      type: "date",
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
      isCriteriaField: true,
    },
    {
      name: "deletedAt",
      label: t("lblCndFieldDeletedAt", { ns: NS }),
      widthClass: "w-[100px]",
      type: "date",
      inGridField: true,
      align: "center",
      display: (_column, value) => (value ? <span className="font-bold text-red-600">DELETED</span> : ""),
      includeConditions: ["isNotNull"],
      ovverideLabelConditions: [{ name: "isNotNull", label: t("lblCndDeletedAt", { ns: NS }) }],
      isCriteriaField: true,
    },
  ];
}

const U3121ProjectList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadRecapOpen, setUploadRecapOpen] = useState(false);
  const [uploadRecapResults, setUploadRecapResults] = useState<U3121UploadFileLineResult[]>([]);
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3121Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findProjectHandlers = useU3121FindProjectHandlers();
  const findCustomerHandlers = useU3121FindCustomerHandlers();
  const findProjectManagerHandlers = useU3121FindProjectManagerHandlers();
  const findLandSurveyorHandlers = useU3121FindLandSurveyorHandlers();

  useEffect(() => {
    const inactiveProjectSuffix = t("valInactive", { ns: NS });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT, {
      search: (criterias) =>
        findProjectHandlers.search([...criterias, U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION]),
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3121ProjectLookupItemToDisplay(item, config, inactiveProjectSuffix),
      itemToSelectedDisplay: (item, config) =>
        u3121ProjectLookupItemToSelectedDisplay(item, config, inactiveProjectSuffix),
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER, {
      search: findCustomerHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: u3121CustomerLookupItemToDisplay,
      itemToSelectedDisplay: u3121CustomerLookupItemToSelectedDisplay,
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT_MANAGER, {
      search: findProjectManagerHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
      itemToSelectedDisplay: u3121EmployeeLookupItemToSelectedDisplay,
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_LAND_SURVEYOR, {
      search: findLandSurveyorHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
      itemToSelectedDisplay: u3121EmployeeLookupItemToSelectedDisplay,
    });
    return () => {
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT_MANAGER);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_LAND_SURVEYOR);
    };
  }, [
    findProjectHandlers.search,
    findCustomerHandlers.search,
    findProjectManagerHandlers.search,
    findLandSurveyorHandlers.search,
    t,
  ]);

  // Initialiser les colonnes au montage et quand la langue (t) change. Ne pas mettre searchStateHandlers/gridStateHandlers en deps
  // pour éviter une boucle infinie (setColumns → dispatch → re-render → nouvelles refs → effet relancé).
  useEffect(() => {
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t]);

  const handlerActions = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (project: U3121Project) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === project.id);
      if (currentIdx !== -1) gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${project.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3121");
      navigate("create");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3121");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => handleDelete.handleDelete(),
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
    onUploadFromFile: () => setUploadDialogOpen(true),
    onCloseInactiveProjects: () => {
      navigate('/U3146/close-inactive-wizard');
    },
  };

  const handleSearch = useU2000SearchHandlers<U3121Project>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => await doSearch(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3121Project>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3121";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const singleProject = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === singleProject.id);
        if (currentIdx !== -1) gridStateHandlers.setCurrentIdx(currentIdx);
        navigate(u2000BuildQuickSearchFormPath(targetLU, singleProject.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  useU2000ExportExcelNotification();

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => gridStateHandlers.getSelectedRows(),
    doDeleteFn: async (id, row) => await doDeleteData(u2000AxiosAuthenticated, id, row),
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: (row as U3121Project).projetNumber, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const refreshListWithCurrentSearchCriteria = useCallback(() => {
    handleSearch.handleSearch(
      searchStateHandlers.extractSearchParameter(),
      gridStateHandlers.extractPagination(gridStateHandlers.getCurrentPage()),
      U2000GridStateEnumPaginationDirection.NEXTPAGE,
    );
  }, [gridStateHandlers, handleSearch, searchStateHandlers]);

  /** BL-0608 : après import multi, filtre liste sur les numéros créés (page 1). */
  const applyImportBatchProjetNumberFilter = useCallback(
    (projetNumbers: string[]) => {
      const column = searchStateHandlers.getColumns().find((c) => c.name === "projetNumber");
      if (!column || projetNumbers.length === 0) {
        refreshListWithCurrentSearchCriteria();
        return;
      }

      const csv = projetNumbers.join(",");
      const displayValue = projetNumbers.join(", ");
      const condition = { name: "in", label: t("lblCndIn", { ns: "common" }), isRequiredValue: true };
      const criteria: Criteria = {
        id: generateTemporaryId(),
        field: column,
        condition,
        value: csv,
        displayValue,
        timestamps: Date.now(),
      };

      searchStateHandlers.selectFilterId(null);
      searchStateHandlers.setCriterias([criteria]);

      const apiField = getCriteriaApiFieldName(column);
      const base = searchStateHandlers.extractSearchParameter();
      handleSearch.handleSearch(
        {
          ...base,
          criterias: [{ field: apiField, operator: "in", value: csv, type: "string" }],
        },
        gridStateHandlers.extractPagination(1),
        U2000GridStateEnumPaginationDirection.NEXTPAGE,
      );
    },
    [gridStateHandlers, handleSearch, refreshListWithCurrentSearchCriteria, searchStateHandlers, t],
  );

  const handleUploadBatchComplete = useCallback(
    (results: U3121UploadFileLineResult[]) => {
      const projetNumbers = u3121ExtractSuccessProjetNumbersFromUploadResults(results);
      if (projetNumbers.length > 0) {
        applyImportBatchProjetNumberFilter(projetNumbers);
      } else {
        refreshListWithCurrentSearchCriteria();
      }
      setUploadRecapResults(results);
      setUploadRecapOpen(true);
    },
    [applyImportBatchProjetNumberFilter, refreshListWithCurrentSearchCriteria],
  );

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3121",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) return null;

  return (
    <>
      <U3121ProjectDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U3121UploadProjectFromFileDialog
        isOpen={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => {
          if (hasRole(U3121ROLES.EDIT)) {
            luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
          } else {
            luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
          }
          luStateHandlers.setActionOriginFrom("");
          handleSearch.handleSearch(
            searchStateHandlers.extractSearchParameter(),
            gridStateHandlers.extractPagination(gridStateHandlers.getCurrentPage()),
            U2000GridStateEnumPaginationDirection.NEXTPAGE
          );
        }}
        onBatchComplete={handleUploadBatchComplete}
      />
      <U3121UploadProjectFromFileRecapDialog
        isOpen={uploadRecapOpen}
        results={uploadRecapResults}
        onClose={() => setUploadRecapOpen(false)}
      />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U3121ProjectListToolBar gridStateHandlers={gridStateHandlers as never} handlerActions={handlerActions as never} />}
        handlerActions={handlerActions}
        label={t("lblProject", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3121ROLES.EXPORT)}
      />
    </>
  );
};

export default U3121ProjectList;
